/**
 * Holds the token from an emailed set-password link for the life of the page.
 *
 * The link is `/set-password#token=<raw>`. Browsers never send a fragment to a
 * server, so the token stays out of access logs and `Referer` headers. `main.ts`
 * calls {@link captureResetToken} before `bootstrapApplication`, because the
 * `APP_INITIALIZER` in `app.config.ts` loads Google Maps before any component
 * runs; reading the token there and clearing the address bar first keeps it from
 * that load, and `replaceState` takes it out of the address bar and the tab's
 * back/forward entry. It does not take it out of the browser's history list,
 * which records the address the link opened before any script runs, so on a
 * shared computer the link stays there until it expires. The token is kept only
 * in this module and sent only in POST bodies.
 */
let resetToken: string | null = null;

const TOKEN_FRAGMENT = /^#token=(.*)$/;

/**
 * Reads the token from a `#token=` fragment, keeps it in memory, and removes the
 * fragment from the address bar. Any other fragment is left alone. An empty token
 * is kept as no token, so the page can show its dead-link state without asking
 * the server.
 */
export function captureResetToken(
  location: Location = window.location,
  history: History = window.history
): void {
  const match = TOKEN_FRAGMENT.exec(location.hash);
  resetToken = match?.[1] || null;
  if (match) {
    history.replaceState(history.state, '', location.pathname + location.search);
  }
}

export function getResetToken(): string | null {
  return resetToken;
}

export function clearResetToken(): void {
  resetToken = null;
}
